/**
 * ScrollToTop.js — Scroll-restoration utility.
 *
 * Resets the window to the top on every route change. Rendered once inside the
 * Router in App.js; renders nothing to the DOM.
 *
 * Uses useLayoutEffect (not history.listen) so the scroll reset runs AFTER the
 * new route has rendered but BEFORE the browser paints — otherwise the new page
 * briefly paints at the previous scroll position (e.g. the footer/bottom) and
 * then jumps up, which looks like the page "loads from the bottom and scrolls up".
 */

// ─── External libraries ───────────────────────────────────────────────────────

import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * ScrollToTop — invisible utility component.
 * Jumps the page to the top whenever the pathname changes.
 *
 * @returns {null}
 */
function ScrollToTop() {

  const { pathname } = useLocation();

  useLayoutEffect(() => {
    // Don't let the browser restore the previous page's scroll position.
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    // Instant jump to the very top (covers window + both scrolling roots for
    // cross-browser safety). Runs before paint, so there is no visible scroll.
    window.scrollTo(0, 0);
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [pathname]);

  return null;
}

// ─── Export ───────────────────────────────────────────────────────────────────
export default ScrollToTop;
